import { FormProvider, getFormProps } from '@conform-to/react'
import { IconButton, Tooltip } from '@radix-ui/themes'
import { animated, useSpring } from '@react-spring/web'
import { useLocation } from '@remix-run/react'
import * as React from 'react'
import { RxHeart, RxHeartFilled } from 'react-icons/rx'
import { useHydrated } from 'remix-utils/use-hydrated'
import { z } from 'zod'
import {
	FormDefaults,
	FormDefaultsHiddenField,
} from '#app/components/common/form-defaults.tsx'
import { type GetProduct } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-product.ts'
import { type Locale } from '#app/models/locale.ts'
import { type App } from '#app/routes/_layout/component.route.tsx'
import {
	ROOT_APP_VIEW,
	type FormEnum,
} from '#app/routes/_layout/route-config.ts'
import { generateItemFormFetcherKey } from '#app/utils/client/generate-item-form-fetcher-key.ts'
import { generateItemFormId } from '#app/utils/client/generate-item-form-id.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useRemoveFromWishlistForm } from '#app/utils/hooks/forms/use-remove-from-wishlist-form.ts'

/* -------------------------------------------------------------------------------------------------
 * Remove from wishlist form
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	productId = 'product-id',
	productVariantId = 'product-variant-id',
}

const FORM: FormEnum = {
	generateId: ({ id }) => generateItemFormId('remove-from-wishlist', id),
	action: '/profile/wishlist',
	generateFetcherKey: ({ id }) =>
		generateItemFormFetcherKey('remove-from-wishlist', id),
	subaction: 'remove-from-wishlist',
	redirect: '/profile/wishlist',
} as const

const schema = z.object({
	...schemaDefault,
	...{
		[INPUTS.productId]: z.number(),
	},
})

type RemoveFromWishlistFormProps = {
	product: GetProduct
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
	loggedIn: boolean
}

const RemoveFromWishlistForm: React.FC<RemoveFromWishlistFormProps> = ({
	product,
	locale,
	setApp,
	loggedIn,
}) => {
	const {
		removeFromWishlistFetcher,
		removeFromWishlistFormValues,
		removeFromWishlistForm,
		removeFromWishlistFields,
		t,
	} = useRemoveFromWishlistForm({ product })

	const location = useLocation()
	const isHydrated = useHydrated()

	const [hover, setHover] = React.useState(false)
	const [springState, toggle] = React.useState(true)

	const { x } = useSpring({
		from: { x: 1 },
		x: springState ? 1 : 0,
		config: { duration: 1000 },
	})

	return (
		<FormProvider context={removeFromWishlistForm.context}>
			<removeFromWishlistFetcher.Form
				method="post"
				action={`/${locale}${FORM.action}${location.search}`}
				{...getFormProps(removeFromWishlistForm)}
				preventScrollReset
			>
				<FormDefaults
					form={removeFromWishlistForm}
					fields={removeFromWishlistFields}
					values={removeFromWishlistFormValues}
				/>
				<FormDefaultsHiddenField
					meta={removeFromWishlistFields[INPUTS.productId]}
				/>
				<animated.div
					style={{
						scale: x.to({
							range: [0, 0.25, 0.35, 0.45, 0.55, 0.65, 0.75, 1],
							output: [1, 0.87, 0.8, 1.2, 0.6, 1.2, 1.13, 1],
						}),
					}}
				>
					<Tooltip
						content={
							loggedIn
								? t('button.removeFromWishlist')
								: t('button.pleaseLogInToUseWishlistFeature')
						}
					>
						<IconButton
							className="app-WishlistButton m-2"
							size="3"
							variant="ghost"
							type={loggedIn ? 'submit' : 'button'}
							onMouseEnter={() => setHover(true)}
							onFocus={() => setHover(true)}
							onMouseLeave={() => setHover(false)}
							onBlur={() => setHover(false)}
							onClick={() => {
								if (!loggedIn) {
									setApp((prev) => ({
										...prev,
										modalView: ROOT_APP_VIEW.loginView,
										displayModal: true,
										options: {
											...prev.options,
											redirect: location.pathname,
											noJs: String(!isHydrated),
										},
									}))
								} else {
									toggle((prev) => !prev)
									setHover(false)
								}
							}}
						>
							{!loggedIn || hover ? (
								<RxHeart size="1.4em" />
							) : (
								<RxHeartFilled size="1.4em" />
							)}
						</IconButton>
					</Tooltip>
				</animated.div>
			</removeFromWishlistFetcher.Form>
		</FormProvider>
	)
}
RemoveFromWishlistForm.displayName = 'RemoveFromWishlistForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type RemoveFromWishlistFormProps,
	RemoveFromWishlistForm,
	INPUTS as REMOVE_FROM_WISHLIST_INPUTS,
	FORM as REMOVE_FROM_WISHLIST_FORM,
	schema as removeFromWishlistSchema,
}
